import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { updateProfile } from 'firebase/auth';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { storage, db, auth } from './firebase';

class ProfilePictureService {
  // Upload profile picture and update user profile
  static async uploadProfilePicture(file, userId) {
    try {
      // Validate file
      if (!file) {
        throw new Error('No file provided');
      }



      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('File size must be less than 5MB');
      }

      // Check file type
      if (file.type && !file.type.startsWith('image/')) {
        throw new Error('File must be an image');
      }

      // Create a unique filename
      const timestamp = Date.now();
      let fileExtension = 'jpg'; // Default extension
      
      // Try to get extension from file name first
      if (file.name && typeof file.name === 'string' && file.name.includes('.')) {
        const nameParts = file.name.split('.');
        if (nameParts.length > 1) {
          fileExtension = nameParts[nameParts.length - 1].toLowerCase();
        }
      } else if (file.type) {
        // Extract extension from MIME type as fallback
        const mimeToExt = {
          'image/jpeg': 'jpg',
          'image/jpg': 'jpg',
          'image/png': 'png',
          'image/gif': 'gif',
          'image/webp': 'webp'
        };
        fileExtension = mimeToExt[file.type.toLowerCase()] || 'jpg';
      }
      
      // Validate extension
      const validExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
      if (!validExtensions.includes(fileExtension)) {
        fileExtension = 'jpg';
      }
      
      const fileName = `profile_${userId}_${timestamp}.${fileExtension}`;
      
      // Create storage reference
      const profilePicRef = ref(storage, `profile-pictures/${fileName}`);
      
      // Upload file
      const snapshot = await uploadBytes(profilePicRef, file);
      
      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Don't update Firebase Auth profile due to URL length limitations
      // Instead, we'll rely on Firestore user document for profile picture
      
      // Update Firestore user document
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        photoURL: downloadURL,
        profilePictureUpdatedAt: new Date()
      });
      
      return {
        success: true,
        url: downloadURL,
        fileName: fileName
      };
      
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      throw error;
    }
  }

  // Delete old profile picture
  static async deleteProfilePicture(photoURL) {
    try {
      if (!photoURL) return;
      
      // Extract file path from URL
      const url = new URL(photoURL);
      const pathMatch = url.pathname.match(/profile-pictures%2F([^?]+)/);
      
      if (pathMatch) {
        const fileName = decodeURIComponent(pathMatch[1]);
        const profilePicRef = ref(storage, `profile-pictures/${fileName}`);
        await deleteObject(profilePicRef);
      }
    } catch (error) {
      console.error('Error deleting old profile picture:', error);
      // Don't throw error as this is cleanup - main operation should still succeed
    }
  }

  // Generate initials avatar URL using a service like UI Avatars
  static generateInitialsAvatar(name, size = 200) {
    if (!name) return null;
    
    const initials = name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .substring(0, 2);
    
    // Using UI Avatars service for consistency
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&size=${size}&background=1976d2&color=ffffff&format=png`;
  }

  // Get user's current profile picture from Firestore or generate initials
  static async getUserProfilePictureAsync(user, size = 200) {
    if (!user) {
      return `https://ui-avatars.com/api/?name=User&size=${size}&background=9e9e9e&color=ffffff&format=png`;
    }

    try {
      // Try to get profile picture from Firestore first
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists() && userDoc.data().photoURL) {
        return userDoc.data().photoURL;
      }
    } catch (error) {
      console.warn('Error fetching user profile from Firestore:', error);
    }

    // Fallback to Auth profile photoURL (though this might be empty now)
    if (user?.photoURL) {
      return user.photoURL;
    }
    
    // Generate initials avatar
    if (user?.displayName) {
      return this.generateInitialsAvatar(user.displayName, size);
    }
    
    if (user?.email) {
      return this.generateInitialsAvatar(user.email.split('@')[0], size);
    }
    
    // Default avatar
    return `https://ui-avatars.com/api/?name=User&size=${size}&background=9e9e9e&color=ffffff&format=png`;
  }

  // Synchronous version for backwards compatibility (checks Auth profile only)
  static getUserProfilePicture(user, size = 200) {
    // This version only checks the Auth user object for immediate rendering
    if (user?.photoURL) {
      return user.photoURL;
    }
    
    if (user?.displayName) {
      return this.generateInitialsAvatar(user.displayName, size);
    }
    
    if (user?.email) {
      return this.generateInitialsAvatar(user.email.split('@')[0], size);
    }
    
    // Default avatar
    return `https://ui-avatars.com/api/?name=User&size=${size}&background=9e9e9e&color=ffffff&format=png`;
  }

  // Compress image before upload (optional but recommended)
  static compressImage(file, maxWidth = 400, quality = 0.8) {
    return new Promise((resolve, reject) => {
      // If file is already small enough, return it as-is
      if (file.size < 100 * 1024) { // Less than 100KB
        resolve(file);
        return;
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        try {
          // Calculate new dimensions maintaining aspect ratio
          const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
          canvas.width = img.width * ratio;
          canvas.height = img.height * ratio;
          
          // Draw and compress
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              // Compression failed, use original file
              resolve(file);
            }
          }, 'image/jpeg', quality);
        } catch (error) {
          console.error('Error compressing image:', error);
          // If compression fails, use original file
          resolve(file);
        }
      };
      
      img.onerror = () => {
        console.error('Error loading image for compression');
        // If image loading fails, use original file
        resolve(file);
      };
      
      try {
        img.src = URL.createObjectURL(file);
      } catch (error) {
        console.error('Error creating object URL:', error);
        resolve(file);
      }
    });
  }
}

export default ProfilePictureService;